import { type InputHTMLAttributes, type ReactNode, useId } from 'react';

export interface AiHint {
  /** Value AI would set if accepted. Displayed verbatim in the hint link. */
  suggestion: string;
  /** Invoked when the user clicks the hint — should call the form's setter for this field. */
  onAccept: () => void;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: ReactNode;
  /**
   * Extra classes for the `<input>` itself. `className` targets the outer
   * wrapper (width/margins), so input-specific styling like centering or a
   * larger font goes here — otherwise it leaks onto the label via inheritance.
   */
  inputClassName?: string;
  /**
   * Marks the field as having been filled by AI at drop time. Renders a
   * subtle amber left-border. Orthogonal to `aiHint`: this says "the value
   * you see came from AI"; the hint says "AI has a different suggestion."
   */
  aiFilled?: boolean;
  /**
   * Shown below the input when AI has a proposal that differs from the
   * current value. Clicking sets the field to the AI value.
   */
  aiHint?: AiHint;
}
export function Input({
  label,
  error,
  icon,
  aiFilled,
  aiHint,
  className = '',
  inputClassName = '',
  id,
  ...props
}: InputProps) {
  // Associate the visible label with the input so clicking it focuses the
  // field and assistive tech reads the visible name (previously the label had
  // no htmlFor, so only a passed aria-label named the input).
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className={`w-full ${className}`}>
      {label &&
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-300 mb-1.5">
          {label}
          {props.required && <span className="ml-1 text-red-400">*</span>}
        </label>
      }
      <div className="relative">
        {icon &&
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {icon}
          </div>
        }
        <input
          id={inputId}
          className={`
            w-full bg-navy-light border rounded-lg
            text-white placeholder-gray-500 text-sm
            focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber
            transition-colors
            ${icon ? 'pl-10' : 'px-3'} py-2.5
            ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : aiFilled ? 'border-navy-lighter border-l-2 border-l-amber/70'
              : 'border-navy-lighter'}
            ${inputClassName}
          `}
          {...props} />

      </div>
      {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
      {!error && aiHint && (
        <button
          type="button"
          onClick={aiHint.onAccept}
          className="mt-1 text-xs text-amber/80 hover:text-amber underline decoration-dotted underline-offset-2 truncate max-w-full text-left"
          title={`Accept AI value: ${aiHint.suggestion}`}
        >
          AI suggests: {aiHint.suggestion}
        </button>
      )}
    </div>);

}